import { useEffect, useState } from "react";
import { api, formatTZS } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar, Legend,
} from "recharts";
import { CalendarCheck, DoorOpen, TrendingUp, BedDouble, Receipt, Wallet } from "lucide-react";

export default function DashboardPage() {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/analytics?period=${period}`);
        setData(data);
        setError(null);
      } catch (e) {
        setError(e?.response?.status === 403 ? "Your role doesn't include the dashboard. Try the Bookings page." : "Failed to load analytics.");
      }
    })();
  }, [period]);

  if (error) return <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 text-amber-900" data-testid="dashboard-forbidden">{error}</div>;
  if (!data) return <p className="text-sm text-[color:var(--muted-text)]">Loading…</p>;

  const chartSeries = data.series.map((s) => ({
    date: new Date(s.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    bookings: s.bookings, revenue: s.revenue, expenses: s.expenses,
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Dashboard</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Bookings, revenue, expenses and occupancy at a glance.</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Today</SelectItem>
            <SelectItem value="week">Last 7 days</SelectItem>
            <SelectItem value="month">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KpiCard label="Bookings" value={data.total_bookings} icon={<CalendarCheck className="w-5 h-5" />} accent="brand" testid="kpi-bookings" />
        <KpiCard label="Room Revenue" value={formatTZS(data.revenue)} icon={<TrendingUp className="w-5 h-5" />} accent="deep" testid="kpi-revenue" />
        <KpiCard label="Room Service" value={formatTZS(data.room_service_revenue)} icon={<Receipt className="w-5 h-5" />} accent="gold" testid="kpi-service" />
        <KpiCard label="Expenses" value={formatTZS(data.expenses)} icon={<Wallet className="w-5 h-5" />} accent="red" testid="kpi-expenses" />
        <KpiCard label="Net" value={formatTZS(data.net)} icon={<TrendingUp className="w-5 h-5" />} accent={data.net >= 0 ? "green" : "red"} testid="kpi-net" />
        <KpiCard label="Occupancy" value={`${data.occupancy}%`} icon={<BedDouble className="w-5 h-5" />} accent="brand" testid="kpi-occupancy" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Currently checked-in" value={data.checked_in} icon={<DoorOpen className="w-5 h-5" />} accent="deep" testid="kpi-checkedin" />
        <KpiCard label="Total room units" value={data.total_units} icon={<BedDouble className="w-5 h-5" />} accent="brand" testid="kpi-units" />
        <KpiCard label="Checked out today" value={data.checked_out_today} icon={<DoorOpen className="w-5 h-5" />} accent="gold" testid="kpi-checkedout" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-heading text-lg text-[color:var(--deep)]">Revenue vs Expenses</CardTitle></CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartSeries}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D97757" stopOpacity={0.4} /><stop offset="100%" stopColor="#D97757" stopOpacity={0} /></linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EF4444" stopOpacity={0.35} /><stop offset="100%" stopColor="#EF4444" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d5" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatTZS(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e5e0d5" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#D97757" strokeWidth={2} fill="url(#rev)" />
                <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#EF4444" strokeWidth={2} fill="url(#exp)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-lg text-[color:var(--deep)]">Bookings per day</CardTitle></CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d5" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e0d5" }} />
                <Bar dataKey="bookings" fill="#2C4C3B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon, accent, testid }) {
  const color =
    accent === "brand" ? "bg-[color:var(--brand)]/10 text-[color:var(--brand)]" :
    accent === "deep" ? "bg-[color:var(--deep)]/10 text-[color:var(--deep)]" :
    accent === "gold" ? "bg-[color:var(--gold)]/15 text-amber-700" :
    accent === "red" ? "bg-red-100 text-red-700" :
    accent === "green" ? "bg-emerald-100 text-emerald-700" :
    "bg-slate-100 text-slate-700";
  return (
    <Card className="hover-lift">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)]">{label}</p>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>{icon}</div>
        </div>
        <p className="mt-3 font-heading text-2xl text-[color:var(--deep)]" data-testid={testid}>{value}</p>
      </CardContent>
    </Card>
  );
}
